import React, { useRef } from 'react';
import { useLevelThreeStyles } from './LevelThreePage.styles';
import { Subtitle2, Image, Body1, Body1Strong, Subtitle1, Button } from '@fluentui/react-components';
import { ChevronDown16Regular, ChevronUp16Regular } from '@fluentui/react-icons';
import { CategorizedSkillResponse, RoleSkill } from '@careerhub/api';
import { filterConstants } from '../../Constant';

interface Iskill {
  filter: string;
  isExpandAll: boolean;
  setShowCollapseAll: (showCollapseAll: boolean) => void;
  categorizedSkills?: CategorizedSkillResponse[];
}

interface seeMore {
  required: boolean;
  notRequired: boolean;
}

export const SkillsMenu: React.FC<Iskill> = (props) => {
  const { categorizedSkills } = props;
  const [seeMoreCategorySkills, setSeeMoreCategorySkills] = React.useState<seeMore[]>(() => {
    const initialState = categorizedSkills
      ? new Array<seeMore>(categorizedSkills.length).fill({
          required: false,
          notRequired: false,
        })
      : [];
    return initialState;
  });

  const [expandCapability, setExpandCapability] = React.useState<boolean[]>(() => {
    const initialState = categorizedSkills ? new Array(categorizedSkills.length).fill(false) : [];
    if (initialState.length > 0) {
      initialState[0] = true;
    }
    return initialState;
  });
  const [shouldCollapseAll, setShouldCollapseAll] = React.useState<boolean>(true);
  const styles = useLevelThreeStyles();
  const isInitialRender = useRef(true);

  const { isExpandAll, setShowCollapseAll } = props;

  React.useEffect(() => {
    if (isInitialRender.current) {
      setShowCollapseAll(false);
      isInitialRender.current = false;
      return; // Skip the first render
    }
    const resetExpandCapability = [...expandCapability];
    if (isExpandAll || shouldCollapseAll) {
      resetExpandCapability.fill(isExpandAll);
      setExpandCapability(resetExpandCapability);
      setShouldCollapseAll(true);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isExpandAll]);

  const getCapabilitySkills = (skills: RoleSkill[], seeMore: boolean, isRequired: boolean, index: number) => {
    return (
      <>
        {skills.length > 0 ? (
          <div className={styles.section}>
            <div className={styles.textContentsContainer}>
              {skills.slice(0, seeMore ? skills.length : 6).map((skill) => (
                <div className={styles.textContents}>
                  <Body1Strong as="h5" className={styles.skillHeader}>
                    {skill.name}
                  </Body1Strong>
                  <Body1 className={styles.skillDescription}>{skill.description}</Body1>
                </div>
              ))}
            </div>
            {skills.length > 6 && (
              <Button
                className={styles.seeMoreButton}
                appearance="transparent"
                size="large"
                onClick={() => {
                  setSeeMoreCategorySkills((prevState) => {
                    return prevState.map((item, idx) =>
                      idx === index
                        ? {
                            required: isRequired ? !item.required : item.required,
                            notRequired: isRequired ? item.notRequired : !item.notRequired,
                          }
                        : item
                    );
                  });
                }}
              >
                {seeMore ? 'See less' : 'See more'}
              </Button>
            )}
          </div>
        ) : (
          <></>
        )}
      </>
    );
  };
  return (
    <div>
      {props.categorizedSkills &&
        props.categorizedSkills.map((categorizedSkill: CategorizedSkillResponse, index) => {
          const { capability } = categorizedSkill;
          const capabilityName = capability.name.split('_')[0];
          const toggleCapability = () => {
            const updatedArray = [...expandCapability];
            updatedArray[index] = !expandCapability[index];
            const isAtleastOneCapabilityCollapsed = updatedArray.some((capabilityExpand) => capabilityExpand == false);
            if (!isAtleastOneCapabilityCollapsed && props.isExpandAll == false) {
              props.setShowCollapseAll(true);
            } else if (isAtleastOneCapabilityCollapsed && props.isExpandAll == true) {
              setShouldCollapseAll(false);
              props.setShowCollapseAll(false);
            }
            setExpandCapability(updatedArray);
          };
          const capabilityContentId = `capability-${index}-content`;
          return (
            <div>
              <h3 className={styles.capabilityHeadingContainer} aria-label={capabilityName}>
                <button
                  type="button"
                  className={styles.capabilityContainer}
                  aria-expanded={expandCapability[index]}
                  aria-controls={capabilityContentId}
                  onClick={toggleCapability}
                  aria-label={`${capabilityName}. ${capability.description}`}
                >
                  <div>
                    <Subtitle1 as="span" className={styles.capabilityHeader}>
                      {capabilityName}
                    </Subtitle1>
                    <Body1 className={styles.capabilityDescription}>{capability.description}</Body1>
                  </div>
                  <div aria-hidden="true">
                    {expandCapability[index] ? <ChevronUp16Regular /> : <ChevronDown16Regular />}
                  </div>
                </button>
              </h3>
              {expandCapability[index] && (
                <div id={capabilityContentId}>
                  {capability.requiredUponEntrySkills.length > 0 && props.filter != filterConstants.notRequired && (
                    <div>
                      <div className={styles.skillsSubtitleContainer}>
                        <Image src="../../../assets/Rectangle.svg" alt="" aria-hidden="true" />
                        <Subtitle2 as="h4" className={styles.skillsSubtitle}>
                          {filterConstants.required}
                        </Subtitle2>
                      </div>
                      {getCapabilitySkills(
                        capability.requiredUponEntrySkills,
                        seeMoreCategorySkills[index].required,
                        true,
                        index
                      )}
                    </div>
                  )}
                  {capability.notRequiredUponEntrySkills.length > 0 && props.filter != filterConstants.required && (
                    <div>
                      <div className={styles.skillsSubtitleContainer}>
                        <Image src="../../../assets/Rectangle.svg" alt="" aria-hidden="true" />
                        <Subtitle2 as="h4" className={styles.skillsSubtitle}>
                          {filterConstants.notRequired}
                        </Subtitle2>
                      </div>
                      {getCapabilitySkills(
                        capability.notRequiredUponEntrySkills,
                        seeMoreCategorySkills[index].notRequired,
                        false,
                        index
                      )}
                    </div>
                  )}
                </div>
              )}
            </div>
          );
        })}
    </div>
  );
};
